import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";

import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import WorkbookPage from "./pages/WorkbookPage";
import WorkbookCatalog from "./pages/WorkbookCatalog";
import BundleDetail from "./pages/BundleDetail";
import BundleLibrary from "./pages/BundleLibrary";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import CommunityPage from "./pages/CommunityPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminDashboard from "./pages/AdminDashboard";
import CourseResume from "./pages/CourseResume";
import CoursesPage from "./pages/CoursesPage";
import CourseEnrollPage from "./pages/CourseEnrollPage";
import EpisodePlayer from "./pages/EpisodePlayer";
import MyCoursesPage from "./pages/MyCoursesPage";
import TrainingPage from "./pages/TrainingPage";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import SessionGuard from "./components/SessionGuard";
import ProfileGuard from "./components/ProfileGuard";
import ScrollManager from "./components/ScrollManager";

// A small redirect that forwards the old /course/:courseSlug/:episodeSlug
// links to the new /courses/... path, preserving both params.
function EpisodeRedirect() {
  const { courseSlug, episodeSlug } = useParams();
  return <Navigate to={`/courses/${courseSlug}/${episodeSlug}`} replace />;
}

// /resources and /resources/:slug are rendered by the BACKEND (HtmlPage.cs)
// and reach the browser through the Netlify proxy rules in public/_redirects.
// The SPA copies of these pages are gone: they predated the email gate, still
// expected a file_url the API no longer returns, and rendered a Download button
// that did nothing.
//
// In production nothing routes here — Navbar and Footer link with plain anchors
// on purpose — so this exists for local dev, where there is no proxy and a
// client-side route change would otherwise land on the dead page.
function ResourceRedirect() {
  useEffect(() => {
    const here = window.location.pathname + window.location.search;
    const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
    const api = process.env.REACT_APP_API_URL;

    // Locally there is no proxy, so the same path would come straight back here
    // and loop. Go to the backend's own origin instead. In production a real
    // document request is all that is needed: the proxy serves the backend page.
    window.location.replace(local && api ? `${api}${here}` : here);
  }, []);

  return <div className="cdh-state">Opening…</div>;
}

function App() {
  return (
    <Router>
      {/* Turns an expired session into an in-app redirect. Must sit inside
          the Router: it reads the current URL and pushes history rather than
          reloading the document, which is what api/client.js used to do. */}
      <SessionGuard />
      {/* Sends a signed-in user with no WhatsApp number to /complete-profile.
          Catches Google sign-ups, which cannot collect one, and everyone who
          registered before the number was required. */}
      <ProfileGuard />
      {/* Decides where each page starts: restore on back/forward, top on a new
          page, untouched on a same-page URL change. Replaces the per-page
          scroll-to-top effects, which fought the global smooth scrolling. */}
      <ScrollManager />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:slug" element={<ProjectDetailPage />} />
        <Route path="/resources" element={<ResourceRedirect />} />
        <Route path="/resources/:slug" element={<ResourceRedirect />} />
        <Route path="/complete-profile" element={<CompleteProfilePage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/workbooks" element={<WorkbookCatalog />} />
        <Route
          path="/workbooks/bundle/:track/library"
          element={<BundleLibrary />}
        />
        <Route path="/workbooks/bundle/:track" element={<BundleDetail />} />
        <Route path="/workbooks/:slug" element={<WorkbookPage />} />
        <Route path="/courses" element={<CoursesPage />} />
        <Route path="/training" element={<TrainingPage />} />
        <Route path="/my-courses" element={<MyCoursesPage />} />
        {/* Old bookmark/link compatibility: /library moved to /my-courses. */}
        <Route path="/library" element={<Navigate to="/my-courses" replace />} />
        <Route path="/courses/:trackId" element={<CourseEnrollPage />} />
        <Route path="/course/:courseSlug" element={<CourseResume />} />
        <Route
          path="/courses/:courseSlug/:episodeSlug"
          element={<EpisodePlayer />}
        />
        <Route
          path="/course/:courseSlug/:episodeSlug"
          element={<EpisodeRedirect />}
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
      </Routes>
    </Router>
  );
}

export default App;
